// ============================================================================
// CALENDAR-DATE HELPERS
//
// A trip's dates are calendar dates, not instants. "2026-07-28" is the 28th
// wherever the traveller is standing; it is not a moment in time that can shift
// across a midnight boundary.
//
// JavaScript makes that easy to get wrong in one specific way, and the codebase
// had it in five places (JAR-610):
//
//   new Date('2026-07-28')              // UTC midnight
//   new Date('2026-07-28T00:00:00')     // LOCAL midnight
//   date.toISOString().split('T')[0]    // formats in UTC
//
// Mixing a local-midnight Date with UTC formatting shifts the day backwards for
// every timezone east of Greenwich. `buildTripFromPlan` did exactly that, so a
// trip starting 2026-07-28 was built with day 1 on 2026-07-27 in London,
// Berlin, Tokyo, Sydney and Kiritimati alike — most of the world's population.
// It passed CI forever because CI runs in UTC.
//
// Parse and format on the same side of that line, and the class of bug goes
// away. These two functions are that line.
// ============================================================================

/**
 * Format a Date as YYYY-MM-DD using its LOCAL calendar fields.
 *
 * Returns '' rather than throwing when handed something that is not a Date.
 * No current caller can reach that — every one passes a `new Date(...)` or a
 * `fromLocalYmd` result — but the module's contract is "a missing date is
 * rejected, not thrown", and a contract that holds for four of its six exports
 * is the kind that gets relied on and then found not to hold. '' is the same
 * rejection `addLocalDays` already returns, so it stays distinguishable from a
 * real date instead of becoming 'NaN-NaN-NaN'.
 */
export const toLocalYmd = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

/**
 * Parse YYYY-MM-DD as a LOCAL time on that calendar date.
 *
 * Anchored at noon rather than midnight on purpose: day arithmetic that lands
 * on a DST spring-forward loses an hour, and from midnight that rolls the date
 * back to the previous day. From noon there are twelve hours of slack in either
 * direction, which no real DST shift exhausts.
 */
export const fromLocalYmd = (ymd: string): Date => {
  // A missing date is a rejected date, not a thrown one. Trip dates are
  // optional all the way through — tripLifecycle guards `!trip.dates?.checkin`,
  // so a trip without them is reachable by construction — and `.split` on
  // undefined is a TypeError raised during render, which unmounts the React
  // tree rather than showing one bad card. Guarding here rather than at the
  // half-dozen call sites is the point of the module: every one of them routes
  // through this function (JAR-703 review, M2).
  if (typeof ymd !== 'string') return new Date(NaN);
  // Callers pass both bare dates and full datetimes — getDateArray is invoked
  // with '2026-06-03T12:00:00' in places. Take the calendar part and ignore the
  // rest, rather than splitting on '-' across the whole string and producing
  // NaN for the day.
  const [y, m, d] = ymd.split('T')[0].split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return new Date(NaN);
  }
  // Out-of-range parts are rejected rather than normalised. The Date
  // constructor rolls them over silently — '2026-13-01' becomes 2027-01-01,
  // '2026-02-30' becomes March 2nd, and years 0–99 map into the 1900s, so
  // '0000-01-01' lands in 1900. That last one is not academic: a tampered
  // localStorage trip or a crafted plan date gives getDateArray a 46,000-day
  // range and the render freezes building it. Finite is not the same as valid.
  if (!isCalendarParts(y, m, d)) {
    return new Date(NaN);
  }
  return new Date(y, m - 1, d, 12);
};

// The outer bounds are deliberately loose — this rejects nonsense, it does not
// enforce a product rule about how far ahead a trip may be planned.
const MIN_YEAR = 1900;
const MAX_YEAR = 2200;

const isCalendarParts = (y: number, m: number, d: number): boolean => {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (y < MIN_YEAR || y > MAX_YEAR) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  // Reject a day that does not exist in that month (Feb 30, Apr 31). The
  // constructor would roll it into the next month instead.
  return new Date(y, m - 1, d, 12).getMonth() === m - 1;
};

/**
 * True for a string this module will parse as a calendar date.
 *
 * The grammar here is deliberately the SAME grammar fromLocalYmd accepts, and
 * that is the whole point of it living next to it. The guard this replaces sat
 * in utils/index.ts as `isBareCalendarDate` and required zero-padding, while the
 * parser it dispatched to did not — so '2026-7-28' failed the guard, fell
 * through to `new Date('2026-7-28')`, and rendered correctly only because V8
 * happens to parse that non-ISO shape as local time rather than UTC (JAR-610
 * L6). A guard and a parser in different files, disagreeing about what they
 * both call a calendar date, is the same two-copies problem this module exists
 * to end.
 *
 * Shape only. Whether the parts are a real date is fromLocalYmd's answer, and
 * asking twice is how the two drift again.
 */
export const isCalendarDate = (s: string): boolean =>
  typeof s === 'string' && /^\d{4}-\d{1,2}-\d{1,2}$/.test(s.trim());

/**
 * Parse a value that may be either a calendar date or an instant.
 *
 * A bare YYYY-MM-DD goes through the local-calendar path; anything else — a
 * full ISO timestamp, an epoch string — is an instant and is parsed as one.
 */
export const parseDateInput = (s: string): Date =>
  // null needs its own rejection: new Date(null) is EPOCH — a valid, wrong
  // 1970 date that flows silently into "days until" math (≈ -20,600) and makes
  // a dateless trip read as decades past, where undefined at least fails loud
  // as an Invalid Date. Rejected like every other missing date in this module.
  s == null ? new Date(NaN)
  : isCalendarDate(s) ? fromLocalYmd(s.trim())
  : new Date(s);

/**
 * A live Date reduced to the same anchor a parsed calendar date gets.
 *
 * For comparing "is this calendar date before/after today". Both sides have to
 * be anchored the same way or the comparison is off by the anchor: a noon-parsed
 * check-in date sits twelve hours AFTER a midnight-reduced `now`, so the trip's
 * own first day reads as still in the future. That is not hypothetical — it is
 * what this consolidation broke on the first pass, and three suites caught it
 * (JAR-703). Anchoring through the same parse is what makes the comparison mean
 * what it reads like.
 */
export const localDayAnchor = (d: Date): Date => fromLocalYmd(toLocalYmd(d));

/** Today's calendar date where the user actually is. */
/**
 * The calendar date inside whatever core sent for a date.
 *
 * core answers a trip's start_date as an ISO datetime ("2026-05-02T00:00:00Z")
 * about as often as a plain day, and a trip's dates are days: everything here
 * compares them as strings, and `<input type="date">` REFUSES a value with a
 * time in it, rendering blank rather than complaining. That is what left Edit
 * trip's Starts and Ends empty for a trip that has dates (JAR-2625).
 *
 * The first ten characters, not a Date round-trip: parsing "2026-05-02T00:00:00Z"
 * and formatting it locally moves the day back one for everyone west of UTC,
 * which is the exact mistake the header of this file exists to stop.
 */
export const ymdFrom = (value: string | undefined | null): string => (value ? value.slice(0, 10) : '');

export const todayYmd = (): string => toLocalYmd(new Date());

/** Shift a calendar date by whole days, staying on the calendar. */
export const addLocalDays = (ymd: string, days: number): string => {
  const d = fromLocalYmd(ymd);
  // fromLocalYmd rejects bad input with an Invalid Date, and setDate leaves it
  // invalid — but toLocalYmd would then format it as the string "NaN-NaN-NaN",
  // which passes for a calendar date at a glance and would flow onward as one.
  // Propagate the rejection instead of laundering it into a plausible-looking
  // value.
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return toLocalYmd(d);
};

/**
 * Whether the calendar days from `from` to `to` fall inside a trip's dates, both
 * ends included (JAR-1914). A date change moves a trip's planned days but never
 * a flight or a stay (core#890), so those are the two that can end up outside.
 *
 * Anything it cannot read as a date answers true: a trip without dates, or an
 * item without one, has nothing to be outside of, and a false flag would tell
 * the person to fix something that is fine. Compared as parsed days, not as
 * strings, so an unpadded '2026-6-1' sorts where it belongs.
 */
export const withinTripDates = (
  trip: { checkin: string; checkout: string },
  from: string,
  to: string = from,
): boolean => {
  const [start, end, first, last] = [trip.checkin, trip.checkout, from, to].map((d) => fromLocalYmd(d).getTime());
  if ([start, end, first, last].some(Number.isNaN)) return true;
  return start <= first && last <= end;
};
